import {
  ChatMessage,
  Citation,
  Conversation,
  ConversationRequest,
  FrontEndSettings,
} from "./models";

// API base URL - empty string for same-origin, or full URL for cross-origin
const API_BASE_URL = import.meta.env.VITE_API_DOCUMENT_SERVICE_URL || '';

// Helper to build headers with optional auth token
const buildHeaders = (token?: string, additionalHeaders?: Record<string, string>): HeadersInit => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

// Feedback types
export interface FeedbackRequest {
  conversation_id: string;
  message_id: string;
  question: string;
  answer: string;
  citations: Citation[];
  feedback_type: 'positive' | 'negative';
  categories?: string[];
  text?: string;
  expanded_mode?: boolean;
  groundedness?: {
    confidence: 'high' | 'medium' | 'low';
    reasoning?: string;
  };
}

export async function submitFeedback(feedback: FeedbackRequest, token?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(feedback),
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    return false;
  }
}

export async function callConversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal,
  token?: string
): Promise<Response> {
  const response = await fetch(`${API_BASE_URL}/api/conversation`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({
      messages: options.messages,
      conversation_id: options.id,
      expert_mode: options.expert_mode,
      skip_clarification: options.skip_clarification,
    }),
    signal: abortSignal,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }

  return response;
}

export type UserInfo = {
  access_token: string;
  expires_on: string;
  id_token: string;
  provider_name: string;
  user_claims: any[];
  user_id: string;
};

export async function getUserInfo(): Promise<UserInfo[]> {
  try {
    const response = await fetch("/.auth/me");
    if (!response.ok) {
      console.log(
        "No identity provider found. Access to chat will be blocked."
      );
      return [];
    }
    const payload = await response.json();
    return payload;
  } catch (e) {
    return [];
  }
}

export async function checkAuthEnforced(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/checkauth`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const config = await response.json(); // Parse JSON response
    return config.is_auth_enforced;
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
    return true; // Return true because we need to enforce auth by default
  }
}

export async function getAssistantTypeApi() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/assistanttype`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const config = await response.json(); // Parse JSON response
    return config;
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
    return null; // Return null or some default value in case of error
  }
}

export const historyRead = async (convId: string, token?: string): Promise<ChatMessage[]> => {
  const response = await fetch(`${API_BASE_URL}/api/history/read`, {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: buildHeaders(token),
  })
    .then(async (res) => {
      if (!res) {
        return [];
      }
      const payload = await res.json();
      const messages: ChatMessage[] = [];
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            date: msg.createdAt,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
          };
          messages.push(message);
        });
      }
      return messages;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.");
      return [];
    });
  return response;
};

export const historyList = async (
  offset = 0,
  token?: string
): Promise<Conversation[] | null> => {
  let response = await fetch(`${API_BASE_URL}/api/history/list?offset=${offset}`, {
    method: "GET",
    headers: buildHeaders(token),
  })
    .then(async (res) => {
      let payload = await res.json();
      if (!Array.isArray(payload)) {
        console.error("There was an issue fetching your data.");
        return null;
      }
      const conversations: Conversation[] = payload.map((conv: any) => {
        const conversation: Conversation = {
          id: conv.id,
          title: conv.title,
          date: conv.createdAt,
          updatedAt: conv?.updatedAt,
          messages: [],
        };
        return conversation;
      });
      return conversations;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.", _err);
      return null;
    });
  return response;
};

export const historyUpdate = async (
  messages: ChatMessage[],
  convId: string,
  token?: string
): Promise<Response> => {
  const response = await fetch(`${API_BASE_URL}/api/history/update`, {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages,
    }),
    headers: buildHeaders(token),
  })
    .then(async (res) => {
      return res;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyRename = async (
  convId: string,
  title: string,
  token?: string
): Promise<Response> => {
  const response = await fetch(`${API_BASE_URL}/api/history/rename`, {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      title: title,
    }),
    headers: buildHeaders(token),
  })
    .then((res) => {
      return res;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDelete = async (convId: string, token?: string): Promise<Response> => {
  const response = await fetch(`${API_BASE_URL}/api/history/delete`, {
    method: "DELETE",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: buildHeaders(token),
  })
    .then((res) => {
      return res;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDeleteAll = async (token?: string): Promise<Response> => {
  const response = await fetch(`${API_BASE_URL}/api/history/delete_all`, {
    method: "DELETE",
    body: JSON.stringify({}),
    headers: buildHeaders(token),
  })
    .then((res) => {
      return res;
    })
    .catch((_err) => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export async function getFrontEndSettings(token?: string): Promise<FrontEndSettings> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/history/frontend_settings`, {
      method: "GET",
      headers: buildHeaders(token),
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const responseJSON = await response.json();
    return responseJSON
  } catch (error) {
    console.error("Failed to fetch Front End Settings:", error);
    return { CHAT_HISTORY_ENABLED: false };
  }
}
